// src/components/team/ImportAuction.jsx
import React, { useMemo, useState } from "react";
import { supabase } from "../../lib/supabaseClient";

/**
 * 🔧 AJOUTER UNE MAISON ?
 * 1) Ajoute une entrée dans HOUSES ci-dessous
 * 2) Renseigne: label, edgeFn, domainRe, examples[], defaults, extractAuctionName(url)
 * 3) (optionnel) parseHints(url) si tu veux logguer des hints spécifiques
 */
const HOUSES = {
  christies: {
    label: "Christie’s",
    edgeFn: "import_christies",
    domainRe: /christies\.com/i,
    examples: [
      "https://www.christies.com/en/auction/21st-century-evening-sale-12345",
      "https://www.christies.com/en/auction/21st-century-evening-sale-12345/browse-lots",
    ],
    defaults: { city: "New York", country: "United States" },
    extractAuctionName: (url) => {
      try {
        // /auction/<slug> or /auction/<slug>/browse-lots
        const m = url.match(/\/auction\/([^/?#]+)/i);
        if (m) {
          const slug = decodeURIComponent(m[1]).replace(/-/g, " ");
          return slug
            .split(" ")
            .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : ""))
            .join(" ");
        }
      } catch {}
      return "Christie’s Sale";
    },
  },
  piasa: {
    label: "Piasa",
    edgeFn: "import_piasa",
    domainRe: /piasa\.fr/i,
    examples: [
      "https://www.piasa.fr/fr/auctions/collection-particuliere-de-photograp",
    ],
    defaults: { city: "Paris", country: "France" },
    extractAuctionName: (url) => {
      try {
        const u = new URL(url);
        // /fr/auctions/collection-particuliere-de-photograp
        const m = u.pathname.match(/\/auctions\/([^/?#]+)/i);
        if (m) {
          return decodeURIComponent(m[1])
            .replace(/-/g, " ")
            .replace(/\b\w/g, (c) => c.toUpperCase());
        }
      } catch {}
      return "Vente Piasa";
    },
  },

  phillips: {
    label: "Phillips",
    edgeFn: "import_phillips",
    domainRe: /phillips\.com/i,
    examples: [
      "https://www.phillips.com/auction/NY011725",
      "https://www.phillips.com/auctions/NY011725",
      "https://www.phillips.com/detail/222748",
    ],
    defaults: { city: "New York", country: "United States" },
    extractAuctionName: (url) => {
      try {
        // Match sale ID style like NY011725 or HK090124
        const m = url.match(/(?:auction|auctions)\/([A-Z]{2}\d{6})/i);
        if (m) return `Phillips ${m[1]}`;
      } catch {}
      return "Phillips Sale";
    },
  },
  artcurial: {
    label: "Artcurial Paris",
    edgeFn: "import_artcurial",
    domainRe: /artcurial\.com/i,
    examples: [
      "https://www.artcurial.com/ventes/6281#catalogue",
      "https://www.artcurial.com/ventes/6281-art-contemporain",
    ],
    defaults: { city: "Paris", country: "France" },
    extractAuctionName: (url) => {
      try {
        const u = new URL(url);
        const path = u.pathname;
        const mSale = path.match(/\/ventes\/(\d+)(?:-([a-z0-9-]+))?/i);
        if (mSale) {
          const id = mSale[1];
          const slug = mSale[2];
          if (slug) {
            const pretty = decodeURIComponent(slug)
              .split("-")
              .filter(Boolean)
              .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
              .join(" ");
            return `${pretty} (Vente ${id})`;
          }
          return `Vente ${id}`;
        }
        const mLot = path.match(/\/ventes\/(\d+)\/lots?\/[^/#?]+/i);
        if (mLot) {
          return `Vente ${mLot[1]}`;
        }
      } catch {}
      return "Vente Artcurial";
    },
   },

  millon: {
    label: "Millon",
    edgeFn: "import_millon",
    domainRe: /millon\.com/i,
    examples: [
      "https://www.millon.com/catalogue/vente4113-millon-access-tableaux-du-xix-et-xxe-siecles#catalog",
    ],
    defaults: { city: "Neuilly-sur-Marne", country: "France" },
    extractAuctionName: (url) => {
      try {
        // /catalogue/venteXXXX-<slug>...
        const m = url.match(/\/catalogue\/vente\d+-([^#/?]+)/i);
        if (m) {
          const slug = decodeURIComponent(m[1]).replace(/-/g, " ");
          return slug
            .split(" ")
            .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : ""))
            .join(" ");
        }
      } catch {}
      return "Vente Millon";
    },
  },
};

// util
const todayISO = () => new Date().toISOString().split("T")[0];

const ImportAuction = () => {
  const [houseKey, setHouseKey] = useState("auto"); // "auto" | key in HOUSES
  const [url, setUrl] = useState("");
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  // Détection auto à partir de l’URL
  const autoHouseKey = useMemo(() => {
    const u = url || "";
    for (const k of Object.keys(HOUSES)) {
      if (HOUSES[k].domainRe.test(u)) return k;
    }
    return null;
  }, [url]);

  const effectiveHouseKey = houseKey === "auto" ? autoHouseKey : houseKey;
  const house = effectiveHouseKey ? HOUSES[effectiveHouseKey] : null;

  async function handleImport() {
    setResult(null);
    if (!url.trim()) {
      alert("Merci d’indiquer l’URL de la vente.");
      return;
    }
    if (!house) {
      alert("Impossible d’identifier la maison. Sélectionne-la manuellement.");
      return;
    }
    if (!house.domainRe.test(url)) {
      alert(`L’URL ne correspond pas à ${house.label}.`);
      return;
    }

    const auction_name = house.extractAuctionName(url);
    const payload = {
      auction_url: url,
      auction_name,
      city: house.defaults.city,
      country: house.defaults.country,
      date_start: todayISO(),
      house_name: house.label,
      house_key: effectiveHouseKey,
    };

    setLoading(true);
    setStatus("🔍 Analyse de la page en cours…");

    try {
      console.log("🚀 Invoke", house.edgeFn, payload);
      const { data, error } = await supabase.functions.invoke(house.edgeFn, {
        body: payload,
      });
      if (error) throw error;

      setResult(data || {});
      const inserted = data?.inserted ?? data?.lots_inserted ?? 0;
      const skipped = data?.skipped ?? data?.lots_skipped ?? 0;
      const found = data?.lots_found ?? data?.found ?? inserted + skipped;
      setStatus(`✅ ${inserted} lot(s) importé(s) • ${skipped} ignoré(s) • ${found} trouvés`);
    } catch (e) {
      console.error("❌ Import error:", e);
      const msg = e?.message || String(e);
      if (/400/.test(msg)) setStatus("❌ 400 – URL invalide ou page sans lots.");
      else if (/500/.test(msg)) setStatus("❌ 500 – Erreur serveur (réessaie).");
      else if (/network|fetch/i.test(msg)) setStatus("❌ Erreur réseau.");
      else setStatus(`❌ ${msg}`);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="border border-gray-200 rounded-xl p-6 bg-white shadow-sm">
      <h2 className="text-xl font-semibold mb-3">🔨 Importer une vente (multi-maisons)</h2>
      <p className="text-sm text-gray-600 mb-4">
        Colle l’URL publique de la vente. La maison est détectée automatiquement
        (ou choisis-la dans la liste).
      </p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
        <div className="md:col-span-1">
          <label className="text-xs text-gray-600">Maison</label>
          <select
            value={houseKey}
            onChange={(e) => setHouseKey(e.target.value)}
            className="mt-1 w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
          >
            <option value="auto">
              🧠 Détection automatique {autoHouseKey ? `→ ${HOUSES[autoHouseKey].label}` : ""}
            </option>
            {Object.entries(HOUSES).map(([k, v]) => (
              <option key={k} value={k}>
                {v.label}
              </option>
            ))}
          </select>
        </div>

        <div className="md:col-span-2">
          <label className="text-xs text-gray-600">URL de la vente</label>
          <input
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://…"
            className="mt-1 w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
          />
        </div>
      </div>

      <details className="mb-4 text-xs text-gray-500">
        <summary className="cursor-pointer hover:text-gray-700">
          Exemples d’URL par maison
        </summary>
        <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-3 pl-1">
          {Object.entries(HOUSES).map(([k, v]) => (
            <div key={k} className="bg-gray-50 p-3 rounded border">
              <div className="font-medium mb-1">{v.label}</div>
              <ul className="list-disc ml-5 space-y-1">
                {v.examples.map((ex, i) => (
                  <li key={i} className="break-all">{ex}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </details>

      <button
        onClick={handleImport}
        disabled={loading || !url.trim() || !house}
        className="w-full bg-black text-white px-5 py-3 rounded-lg font-medium hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition"
      >
        {loading ? (
          <span className="flex items-center justify-center gap-2">
            <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
            Import en cours…
          </span>
        ) : (
          "🚀 Lancer l’import"
        )}
      </button>

      {status && (
        <div
          className={`mt-4 p-3 rounded-lg ${
            status.includes("✅")
              ? "bg-green-50 text-green-800 border border-green-200"
              : status.includes("❌")
              ? "bg-red-50 text-red-800 border border-red-200"
              : "bg-blue-50 text-blue-800 border border-blue-200"
          }`}
        >
          {status}
        </div>
      )}

      {result && (
        <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
          <h3 className="font-semibold mb-3 flex items-center gap-2">
            <span>📊</span> Résultat de l’import
          </h3>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="bg-white p-3 rounded border">
              <div className="text-gray-500 text-xs mb-1">Maison</div>
              <div className="font-medium">ID: {result.house_id?.slice(0, 8)}…</div>
            </div>
            <div className="bg-white p-3 rounded border">
              <div className="text-gray-500 text-xs mb-1">Vente</div>
              <div className="font-medium">ID: {result.sale_id?.slice(0, 8)}…</div>
            </div>
            <div className="bg-white p-3 rounded border">
              <div className="text-gray-500 text-xs mb-1">Lots trouvés</div>
              <div className="font-medium text-blue-700">{result.lots_found || 0}</div>
            </div>
            <div className="bg-white p-3 rounded border">
              <div className="text-gray-500 text-xs mb-1">Lots insérés</div>
              <div className="font-semibold text-green-700">{result.inserted || 0}</div>
            </div>
          </div>

          {result.skipped > 0 && (
            <div className="mt-3 text-xs text-gray-600 bg-white p-2 rounded">
              ℹ️ {result.skipped} lot(s) ignoré(s) (déjà présents en base).
            </div>
          )}
        </div>
      )}

      <details className="mt-4 text-xs text-gray-500">
        <summary className="cursor-pointer hover:text-gray-700">
          ❓ L’import ne fonctionne pas ?
        </summary>
        <div className="mt-2 space-y-2 pl-4">
          <div>• Vérifie que l’URL pointe une page « Catalogue » avec des cartes de lots.</div>
          <div>• Regarde les logs de l’edge function dans Supabase pour les détails.</div>
        </div>
      </details>
    </div>
  );
};

export default ImportAuction;
